import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code+newpwd
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      toast.success("If an account exists, a code has been sent to your email");
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/verify", { email, code, new_password: newPwd });
      toast.success("Password updated — you can sign in now");
      nav("/login");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const inputCls = "mt-2 bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]";

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">Recover access</div>
        <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">Reset password</h1>

        {step === 1 ? (
          <form onSubmit={requestCode} className="mt-8 space-y-5" data-testid="forgot-step-email">
            <div>
              <Label className="uppercase text-xs tracking-widest text-white/70">Email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className={inputCls} />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-request-btn" className="w-full bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-11">
              {loading ? "Sending…" : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="mt-8 space-y-5" data-testid="forgot-step-verify">
            <p className="text-sm text-white/60">Check your inbox for a 6-digit code. It expires in 15 minutes.</p>
            <div>
              <Label className="uppercase text-xs tracking-widest text-white/70">6-digit code</Label>
              <Input required minLength={6} maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="forgot-code-input" className={`${inputCls} tracking-[8px] text-center text-2xl font-bold`} />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-widest text-white/70">New password (min 6 chars)</Label>
              <Input required minLength={6} type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="forgot-newpwd-input" className={inputCls} />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-verify-btn" className="w-full bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-11">
              {loading ? "Updating…" : "Update password"}
            </Button>
            <button type="button" onClick={() => setStep(1)} className="text-xs text-white/50 hover:text-white underline">Use a different email</button>
          </form>
        )}

        <p className="mt-6 text-sm text-white/60">
          Remembered it? <Link to="/login" className="text-[#e65f2b] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
